import { InMemoryCache, type Reference } from '@apollo/client';
import { CommentsQuery, GetMicrostoreSitesPaginatedQuery } from 'data/__generated__';
import { uniqBy } from 'lodash';

// to use union in fragments
export const possibleTypes = {
    OpsParameter: ['BooleanOpsParameter', 'NumericalOpsParameter'],
    RitualGame: ['QuizRitualGame', 'WantedRitualGame'],
    TourStop: ['Delivery', 'Task'],
    SetupInstruction: [
        'CheckTemperatureInstruction',
        'CheckBoxTemperatureInstruction',
        'HotDrinksInstruction',
        'InventoryInstruction',
        'ManualReassortInstruction',
        'MicrowaveInstruction',
        'MoveProductsInstruction',
        'PhotosInstruction',
        'RemoveProductsInstruction',
        'StoreProductsInstruction',
        'TheftNoticeInstruction',
        'WaterFountainInstruction',
    ],
    SupplyOrderProductIssue: [
        'BadExpiryDateIssue',
        'BrokenQuantityIssue',
        'DifferentPackagingIssue',
        'DifferentQuantityIssue',
        'NewBarcodeIssue',
        'NotReceivedIssue',
        'UnknownProductIssue',
        'WrongProductIssue',
    ],
    AuditLogPatch: ['AddPatch', 'ReplacePatch', 'RemovePatch', 'MovePatch', 'CopyPatch', 'TestPatch'],
};

const DEFAULT_KEY_ARGS = ['limit', 'sortBy', 'sortOrder', 'filters'];

export const cache = new InMemoryCache({
    possibleTypes,
    typePolicies: {
        BoxProduct: { keyFields: ['boxId', '_id'] },
        OrderProduct: { keyFields: ['orderId', '_id'] },
        ProductStockVariation: { keyFields: ['supplyOrderId', 'productId'] },
        SupplyOrderProduct: { keyFields: ['supplyOrderId', '_id'] },
        ControlPhoto: {
            keyFields: (photo) =>
                photo.taskInstructionId
                    ? ['taskInstructionId', 'type', 'photoIndex']
                    : ['deliveryId', 'setupInstructionId', 'type', 'photoIndex'],
        },
        // Pas d'`_id` : les groupes déclarés dans le code n'existent pas en base, leur identité est le slug.
        PermissionGroup: { keyFields: ['slug'], fields: { permissions: { merge: false } } },
        PermissionCatalogEntry: { keyFields: ['code'] },
        // Jamais normalisé : son `_id` désigne l'instrument (crédit, carte, subvention…), le même sur toutes les
        // transactions qu'il a payées, chacune pour un autre montant.
        TransactionPaymentMethod: { keyFields: false },
        // Jamais normalisé : son `id` n'est unique que dans son modèle Odoo (l'équipe 19 n'est pas le site 19).
        OdooRef: { keyFields: false },
        // Idem : une machine encore sans fiche Odoo a l'`id` 0, partagé par tous ces tickets.
        TicketMachine: { keyFields: false },
        User: {
            fields: {
                permissions: {
                    merge: false,
                },
                effectivePermissions: {
                    merge: false,
                },
                visitedSites: {
                    merge: false,
                },
            },
        },
        SupplyOrder: {
            fields: {
                products: {
                    merge: false,
                },
            },
        },
        Supplier: {
            fields: {
                productsInfoWithStock: {
                    merge: false,
                },
            },
        },
        Query: {
            fields: {
                credits: {
                    keyArgs: false,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.credits || [];
                        const incomingItems = incoming.credits;

                        // Première page (chargement, changement de filtre) : on remplace, sinon les crédits
                        // de l'ancien filtre restent dans la liste.
                        if ((args?.offset ?? 0) === 0) {
                            return { ...incoming, credits: incomingItems };
                        }

                        const newCredits = [...existingItems];
                        const newCreditIds = newCredits.map((credit) => readField('_id', credit));
                        incomingItems.forEach((item: any) => {
                            if (!newCreditIds.includes(readField('_id', item))) {
                                newCredits.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            credits: newCredits,
                        };
                    },
                },
                catalogProducts: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.products || [];
                        const incomingItems = incoming.products;

                        if ((args?.offset ?? 0) === 0) {
                            return { ...incoming, products: incomingItems };
                        }

                        const newProducts = [...existingItems];
                        const newProductIds = newProducts.map((product) => readField('_id', product));
                        incomingItems.forEach((item: any) => {
                            if (!newProductIds.includes(readField('_id', item))) {
                                newProducts.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            products: newProducts,
                        };
                    },
                },
                /**
                 * Pagination de la page Commandes. `keyArgs` sans `offset` : c'est ce qui fait que
                 * `fetchMore` ALLONGE la liste au lieu d'écrire dans une autre entrée de cache. Un
                 * changement de filtre repart d'une entrée neuve, donc d'une page 0.
                 */
                ordersFilter: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        if ((args?.offset ?? 0) === 0) {
                            return incoming;
                        }

                        const existingOrders: Reference[] = existing?.orders || [];
                        const knownIds = new Set(existingOrders.map((order) => readField('_id', order)));
                        const appended = (incoming.orders as Reference[]).filter(
                            (order) => !knownIds.has(readField('_id', order)),
                        );

                        return { ...incoming, orders: [...existingOrders, ...appended] };
                    },
                },
                /**
                 * Pagination du catalogue de saisie d'une commande (popup « Modifier les produits »).
                 * `keyArgs` sans `offset` : c'est ce qui fait que `fetchMore` ALLONGE la grille au lieu
                 * d'écrire dans une autre entrée de cache — sans cette policy, « Charger plus de
                 * produits » partait au serveur sans que rien ne s'ajoute à l'écran. `orderId` EN FAIT
                 * PARTIE : prix et stocks sont résolus sur la commande, deux commandes ne peuvent pas
                 * partager une entrée.
                 */
                orderCatalogue: {
                    keyArgs: ['orderId', ...DEFAULT_KEY_ARGS],
                    merge(existing, incoming, { readField, args }) {
                        if ((args?.offset ?? 0) === 0) {
                            return incoming;
                        }

                        const existingProducts: Reference[] = existing?.products || [];
                        const knownIds = new Set(existingProducts.map((product) => readField('_id', product)));
                        const appended = (incoming.products as Reference[]).filter(
                            (product) => !knownIds.has(readField('_id', product)),
                        );

                        return { ...incoming, products: [...existingProducts, ...appended] };
                    },
                },
                columnProducts: {
                    keyArgs: ['columnId', ...DEFAULT_KEY_ARGS],
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.columnProducts || [];
                        const incomingItems = incoming.columnProducts;

                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                columnProducts: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        const newColumnProducts = [...existingItems];
                        const newProductIds = newColumnProducts.map((cp) => readField('productId', cp));
                        incomingItems.forEach((item: any) => {
                            if (!newProductIds.includes(readField('productId', item))) {
                                newColumnProducts.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            columnProducts: newColumnProducts,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                siteProducts: {
                    keyArgs: ['siteId', ...DEFAULT_KEY_ARGS],
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.siteProducts || [];
                        const incomingItems = incoming.siteProducts;

                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                siteProducts: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        const newSiteProducts = [...existingItems];
                        const newProductIds = newSiteProducts.map((sp) => readField('productId', sp));
                        incomingItems.forEach((item: any) => {
                            if (!newProductIds.includes(readField('productId', item))) {
                                newSiteProducts.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            siteProducts: newSiteProducts,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                payments: {
                    keyArgs: ['stateFilter', 'organizationIdFilter'],
                    merge(existing, incoming, { readField }) {
                        const existingItems = existing?.payments || [];
                        const incomingItems = incoming.payments;
                        const newPayments = [...incomingItems];
                        const newPaymentIds = newPayments.map((payment) => readField('_id', payment));
                        existingItems.forEach((item: any) => {
                            if (!newPaymentIds.includes(readField('_id', item))) {
                                newPayments.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            payments: newPayments,
                        };
                    },
                },
                stockTransfers: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.stockTransfers || [];
                        const incomingItems = incoming.stockTransfers;

                        // A fresh query (offset 0, e.g. after a sort/filter change) replaces the list.
                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                stockTransfers: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        // fetchMore: append the next page below the already-loaded rows.
                        const newStockTransfers = [...existingItems];
                        const newStockTransferIds = newStockTransfers.map((stockTransfer) =>
                            readField('_id', stockTransfer),
                        );
                        incomingItems.forEach((item: any) => {
                            if (!newStockTransferIds.includes(readField('_id', item))) {
                                newStockTransfers.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            stockTransfers: newStockTransfers,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                supplyOrders: {
                    keyArgs: false,
                    merge: false,
                },
                paginatedFilteredSuppliers: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { args, readField }) {
                        const incomingItems = incoming.suppliers;

                        // Première page (chargement initial, ou refetch après un changement de
                        // filtre/tri) : on REMPLACE au lieu d'empiler.
                        if (!args?.offset) {
                            return { ...incoming, suppliers: incomingItems, hasMore: incoming.hasMore };
                        }

                        const newSuppliers = [...(existing?.suppliers || [])];
                        const newSuppliersIds = newSuppliers.map((supplier) => readField('_id', supplier));
                        incomingItems.forEach((item: any) => {
                            if (!newSuppliersIds.includes(readField('_id', item))) {
                                newSuppliers.push(item);
                            }
                        });

                        return { ...incoming, suppliers: newSuppliers, hasMore: incoming.hasMore };
                    },
                },
                auditLogsPaginated: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { args, readField }) {
                        const incomingItems = incoming.auditLogs;

                        // Première page (chargement initial, ou refetch après un changement de
                        // filtre/tri) : on REMPLACE au lieu d'empiler. `PaginatedAuditLogs` n'expose pas
                        // d'offset, on lit donc celui des arguments.
                        if (!args?.offset) {
                            return { ...incoming, auditLogs: incomingItems, hasMore: incoming.hasMore };
                        }

                        const mergedAuditLogs = [...(existing?.auditLogs || [])];
                        const mergedIds = mergedAuditLogs.map((auditLog) => readField('_id', auditLog));
                        incomingItems.forEach((item: any) => {
                            if (!mergedIds.includes(readField('_id', item))) {
                                mergedAuditLogs.push(item);
                            }
                        });

                        return { ...incoming, auditLogs: mergedAuditLogs, hasMore: incoming.hasMore };
                    },
                },
                transactions: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { args, readField }) {
                        const existingItems = existing?.transactions || [];
                        const incomingItems = incoming.transactions;

                        // Première page (chargement initial, ou refetch après un changement de filtre/tri) :
                        // on REMPLACE au lieu d'empiler. `PaginatedTransactions` n'expose pas d'offset, on lit
                        // donc celui des arguments. Sans ce reset, deux vues qui partagent la même entrée de
                        // cache (la section d'une fiche site et la page « voir plus », mêmes `keyArgs`)
                        // accumulent des pages qui ne se suivent pas.
                        if (!args?.offset) {
                            return { ...incoming, transactions: incomingItems, hasMore: incoming.hasMore };
                        }

                        const newTransactions = [...existingItems];
                        const newTransactionIds = newTransactions.map((transaction) => readField('_id', transaction));
                        incomingItems.forEach((item: any) => {
                            if (!newTransactionIds.includes(readField('_id', item))) {
                                newTransactions.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            transactions: newTransactions,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                transactionPayments: {
                    keyArgs: ['limit', 'filters'],
                    merge(existing, incoming, { args, readField }) {
                        // Première page : on REMPLACE, comme pour `transactions` (fiche utilisateur et page
                        // « voir plus » partagent une entrée de cache).
                        if (!args?.offset) {
                            return incoming;
                        }

                        const existingPayments: Reference[] = existing?.transactionPayments || [];
                        const knownIds = new Set(existingPayments.map((payment) => readField('_id', payment)));
                        const appended = (incoming.transactionPayments as Reference[]).filter(
                            (payment) => !knownIds.has(readField('_id', payment)),
                        );

                        return { ...incoming, transactionPayments: [...existingPayments, ...appended] };
                    },
                },
                sitesFilter: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.sites || [];
                        const incomingItems = incoming.sites;

                        // `PaginatedSites` n'expose pas d'offset : on lit celui des arguments.
                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                sites: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        const newSites = [...existingItems];
                        const newSiteIds = newSites.map((site) => readField('_id', site));
                        incomingItems.forEach((item: any) => {
                            if (!newSiteIds.includes(readField('_id', item))) {
                                newSites.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            sites: newSites,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                microstoreColumnsFilter: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.microstoreColumns || [];
                        const incomingItems = incoming.microstoreColumns;

                        // If it's a new request (offset: 0), we replace completely. `PaginatedMicrostoreColumns`
                        // has no `offset` field: read it from the arguments.
                        if ((args?.offset ?? 0) === 0) {
                            return {
                                ...incoming,
                                microstoreColumns: incomingItems,
                                hasMore: incoming.hasMore,
                            };
                        }

                        // Otherwise, we merge the results
                        const newColumns = [...existingItems];
                        const newColumnIds = newColumns.map((column) => readField('_id', column));
                        incomingItems.forEach((item: any) => {
                            if (!newColumnIds.includes(readField('_id', item))) {
                                newColumns.push(item);
                            }
                        });

                        return {
                            ...incoming,
                            microstoreColumns: newColumns,
                            hasMore: incoming.hasMore,
                        };
                    },
                },
                microstoreSitesWithColumnGroupsFilter: {
                    keyArgs: ['filterString', 'stateFilter'],
                    merge(
                        existing: GetMicrostoreSitesPaginatedQuery['microstoreSitesWithColumnGroupsFilter'] | undefined,
                        incoming: GetMicrostoreSitesPaginatedQuery['microstoreSitesWithColumnGroupsFilter'],
                        { readField, args },
                    ) {
                        const existingItems = existing?.sites || [];
                        const incomingItems = incoming.sites;

                        if ((args?.offset ?? 0) === 0) {
                            return { ...incoming, sites: incomingItems };
                        }

                        const newSites = [...existingItems];
                        const newSiteIds = newSites.map((site) => readField('_id', site));
                        incomingItems.forEach((item) => {
                            if (!newSiteIds.includes(readField('_id', item))) {
                                newSites.push(item);
                            }
                        });
                        return {
                            ...incoming,
                            sites: newSites,
                        };
                    },
                },
                users: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.users || [];
                        const incomingItems = incoming.users;

                        if ((args?.offset ?? 0) === 0) {
                            return { ...incoming, users: incomingItems, hasMore: incoming.hasMore };
                        }

                        const newUsers = [...existingItems];
                        const newUserIds = newUsers.map((user) => readField('_id', user));
                        incomingItems.forEach((item: any) => {
                            if (!newUserIds.includes(readField('_id', item))) {
                                newUsers.push(item);
                            }
                        });

                        return { ...incoming, users: newUsers, hasMore: incoming.hasMore };
                    },
                },
                bundlesFilter: {
                    keyArgs: DEFAULT_KEY_ARGS,
                    merge(existing, incoming, { readField, args }) {
                        const existingItems = existing?.bundles || [];
                        const incomingItems = incoming.bundles;

                        if ((args?.offset ?? 0) === 0) {
                            return { ...incoming, bundles: incomingItems, hasMore: incoming.hasMore };
                        }

                        const newBundles = [...existingItems];
                        const newBundleIds = newBundles.map((bundle) => readField('_id', bundle));
                        incomingItems.forEach((item: any) => {
                            if (!newBundleIds.includes(readField('_id', item))) {
                                newBundles.push(item);
                            }
                        });

                        return { ...incoming, bundles: newBundles, hasMore: incoming.hasMore };
                    },
                },
                comments: {
                    keyArgs: false,
                    merge(
                        existing: CommentsQuery['comments'] | undefined,
                        incoming: CommentsQuery['comments'],
                        { readField, args },
                    ) {
                        if ((args?.offset ?? 0) === 0) {
                            return incoming;
                        }

                        const existingComments = existing?.comments ?? [];
                        const mergedComments = uniqBy([...existingComments, ...incoming.comments], (comment) =>
                            readField('_id', comment),
                        );
                        return { ...incoming, comments: mergedComments };
                    },
                },
            },
        },
    },
});
